/**
 * Skill radar chart — pure SVG, no external dep.
 *
 * Renders up to 12 axes (skills), each mapped 0..4 from centre.
 * Two overlapping polygons: ``current`` (filled, accent colour)
 * and an optional ``required`` outline (the role's expected
 * level). Lets a manager see at-a-glance where the employee
 * matches, exceeds, or falls short of the role bar.
 */
import { useMemo } from "react";

export interface RadarPoint {
  label: string;
  current: number; // 0..4
  required?: number; // 0..4 (omit to render only current)
}

interface Props {
  points: RadarPoint[];
  size?: number;
  // Display max — defaults to 4 (our proficiency scale).
  max?: number;
}

export function SkillRadar({ points, size = 360, max = 4 }: Props) {
  const cx = size / 2;
  const cy = size / 2;
  const r = (size / 2) * 0.7; // ring radius

  // Cap to 12 axes — beyond that the labels become illegible. The
  // page-level component picks the most relevant subset (role
  // requirements + top employee skills).
  const data = useMemo(() => points.slice(0, 12), [points]);

  if (data.length < 3) {
    // Radar charts need ≥3 axes to read as a polygon. Fewer
    // skills → render a friendly empty-state instead of a
    // degenerate triangle/line.
    return (
      <div
        style={{ width: size, height: size }}
        className="flex items-center justify-center text-center text-sm text-text-muted px-8"
      >
        Add at least 3 skills to see the radar chart.
      </div>
    );
  }

  const angleFor = (i: number) => (Math.PI * 2 * i) / data.length - Math.PI / 2;
  const point = (i: number, value: number) => {
    const a = angleFor(i);
    const ratio = Math.max(0, Math.min(value / max, 1));
    return [cx + Math.cos(a) * r * ratio, cy + Math.sin(a) * r * ratio];
  };

  const currentPoly = data
    .map((d, i) => point(i, d.current).join(","))
    .join(" ");
  const hasRequired = data.some((d) => typeof d.required === "number");
  const requiredPoly = hasRequired
    ? data
        .map((d, i) => point(i, d.required ?? 0).join(","))
        .join(" ")
    : null;

  // Concentric rings for visual reference (1, 2, 3, 4).
  const rings = Array.from({ length: max }, (_, k) => ((k + 1) / max) * r);

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width="100%"
      height="100%"
      role="img"
      aria-label="Skills radar chart"
      style={{ maxWidth: size, height: "auto" }}
    >
      {/* Rings */}
      {rings.map((ringR, idx) => (
        <circle
          key={idx}
          cx={cx}
          cy={cy}
          r={ringR}
          fill="none"
          stroke="rgb(var(--color-border))"
          strokeDasharray={idx === rings.length - 1 ? "0" : "3 3"}
          strokeWidth={1}
        />
      ))}
      {/* Axes + labels */}
      {data.map((d, i) => {
        const [x, y] = point(i, max);
        const labelOffset = 16;
        const a = angleFor(i);
        const lx = cx + Math.cos(a) * (r + labelOffset);
        const ly = cy + Math.sin(a) * (r + labelOffset);
        return (
          <g key={d.label}>
            <line
              x1={cx}
              y1={cy}
              x2={x}
              y2={y}
              stroke="rgb(var(--color-border))"
              strokeWidth={1}
            />
            <text
              x={lx}
              y={ly}
              fontSize="11"
              textAnchor={
                Math.abs(Math.cos(a)) < 0.2
                  ? "middle"
                  : Math.cos(a) > 0
                    ? "start"
                    : "end"
              }
              alignmentBaseline="middle"
              fill="rgb(var(--color-text-muted))"
            >
              {d.label.length > 18 ? d.label.slice(0, 17) + "…" : d.label}
            </text>
          </g>
        );
      })}
      {/* Required overlay (outline) */}
      {requiredPoly && (
        <polygon
          points={requiredPoly}
          fill="none"
          stroke="rgb(var(--color-text-muted))"
          strokeWidth={1.5}
          strokeDasharray="4 3"
        />
      )}
      {/* Current (filled) */}
      <polygon
        points={currentPoly}
        fill="rgb(var(--color-accent))"
        fillOpacity={0.2}
        stroke="rgb(var(--color-accent))"
        strokeWidth={2}
      />
      {/* Current dots */}
      {data.map((d, i) => {
        const [px, py] = point(i, d.current);
        return (
          <circle
            key={`pt-${i}`}
            cx={px}
            cy={py}
            r={3.5}
            fill="rgb(var(--color-accent))"
          >
            <title>
              {d.label}: {d.current}/{max}
              {typeof d.required === "number"
                ? ` (target ${d.required})`
                : ""}
            </title>
          </circle>
        );
      })}
    </svg>
  );
}
